export interface JobReady {
  key: string;
  payload: unknown;
}

export interface EventSourceLike {
  addEventListener: (type: string, cb: (e: MessageEvent) => void) => void;
  close: () => void;
}

interface TokenResult {
  token: string;
  ns: string;
}

interface ConnectedEvent {
  connId: string;
}

interface ResolverEntry {
  resolve: (r: JobReady) => void;
  immediateCheck: () => Promise<JobReady | null>;
  /** Interval handle for this job's safety probe, or null before it is started.
   *  Held here (not just in awaitJob's local scope) so #onResolverRemoved can tear it
   *  down on the caller-bail path, where awaitJob's own finally never runs. */
  probeTimer: ReturnType<typeof setInterval> | null;
}

interface Deps {
  eventSourceFactory: (url: string) => EventSourceLike;
  fetchFn: typeof fetch;
  getToken: () => Promise<TokenResult>;
  /** Base URL of the SSE gateway. Defaults to window.PMW.streamGatewayUrl. */
  gatewayBaseUrl?: string;
}

/** Idle period (ms) after the last resolver is removed before the EventSource is closed. Exported for tests. */
export const IDLE_CLOSE_DELAY_MS = 2 * 60 * 1000;

/** Interval (ms) for the non-blocking store re-read during an SSE wait, so a missed
 *  job-ready event degrades to "found a bit later" instead of hanging until timeout. */
const STREAM_FASTPATH_PROBE_INTERVAL_MS = 30_000;

/** A promise that never resolves — used so only a real JobReady result wins the race. */
const PENDING_FOREVER = new Promise<JobReady>((_resolve) => {
  // Intentionally left pending — this promise is used as a no-op race arm.
});

/**
 * Thrown by {@link StreamClient.awaitJob} when the one-shot immediate status read
 * rejects. That rejection means the JOB itself errored (the status endpoint returned
 * an error response) — NOT that the SSE connection/subscription failed. The caller
 * uses this to fall back to the legacy loop (which re-fetches and surfaces the job
 * error) WITHOUT logging an SSE-health fallback, keeping `reason=error` reserved for
 * genuine transport failures.
 */
export class ImmediateCheckError extends Error {
  readonly reason: unknown;

  constructor(reason: unknown) {
    super('stream-client: immediate status check rejected');
    this.name = 'ImmediateCheckError';
    this.reason = reason;
  }
}

/**
 * Thrown by {@link StreamClient.awaitJob} when the gateway rejects the /subscribe
 * request (non-2xx). Distinct from a connect failure: the SSE connection itself is
 * up, only the subscription was refused — the caller logs it under its own
 * telemetry reason so the two failure modes are separable in production.
 */
export class SubscribeRejectedError extends Error {
  readonly status: number;

  constructor(status: number) {
    super(`stream-client: subscribe rejected with status ${status}`);
    this.name = 'SubscribeRejectedError';
    this.status = status;
  }
}

export class StreamClient {
  #connId: string | null = null;
  #token: string | null = null;
  #ns: string | null = null;
  #connecting: Promise<void> | null = null;
  /** Resolves the first-connect promise exactly once; cleared after first call. */
  #connectResolve: (() => void) | null = null;
  #es: EventSourceLike | null = null;
  #idleTimer: ReturnType<typeof setTimeout> | null = null;
  readonly #resolvers = new Map<string, ResolverEntry>();
  readonly #gatewayBaseUrl: string;

  constructor(private readonly deps: Deps) {
    this.#gatewayBaseUrl = deps.gatewayBaseUrl ?? window.PMW.streamGatewayUrl;
  }

  /**
   * Drops the pending resolver for `key` without resolving it — used when the
   * caller bails (timeout or stop event) so the orphaned resolver is removed and
   * a late SSE event for that key becomes a no-op. Also allows idle-close to
   * fire if the registry is now empty.
   */
  cancelJob(key: string): void {
    if (this.#resolvers.has(key)) {
      this.#onResolverRemoved(key);
    }
  }

  /** True when an SSE connection is currently established (a connId has been assigned). */
  isConnected(): boolean {
    return this.#connId !== null;
  }

  /**
   * Eagerly establishes the shared SSE connection so a subsequent awaitJob() reuses it
   * instead of paying the cold cross-host handshake (DNS+TCP+TLS + the gateway's
   * `connected` event) inside its 5s connect budget. Fire-and-forget and never throws:
   * a failed warm just means the first job connects cold, exactly as today. Idempotent —
   * the #connecting guard collapses repeat calls. Warming adds no resolver, so it arms
   * the idle-close itself; a real awaitJob cancels that timer and takes over.
   *
   * Concurrent-safety note: if an awaitJob races this warm on the same in-flight connect,
   * warm's .then (attached first) runs on the `connected` event before awaitJob registers
   * its resolver, so warm may schedule an idle-close that awaitJob's earlier synchronous
   * #cancelIdleTimer() does not clear. That stale timer is harmless — its #resolvers.size
   * === 0 guard blocks a wrongful close (the resolver is registered microtasks later, long
   * before the 2-minute delay), and the next #onResolverRemoved reschedules cleanly.
   */
  warm(): void {
    void this.#ensureConnected()
      .then(() => {
        this.#armIdleCloseIfIdle();
      })
      .catch(() => {
        // swallow: warming is best-effort; the first awaitJob retries the connect
      });
  }

  async awaitJob(key: string, immediateCheck: () => Promise<JobReady | null>): Promise<JobReady> {
    this.#cancelIdleTimer();
    await this.#ensureConnected();
    if (!this.#ns) {
      throw new Error('stream-client: namespace is empty after connect');
    }
    const fromEvent = new Promise<JobReady>((resolve) => {
      this.#resolvers.set(key, {resolve, immediateCheck, probeTimer: null});
    });
    try {
      await this.#subscribe(key);
    } catch (e) {
      // Without the subscription no ready event will ever arrive for this key.
      // Drop the orphaned resolver (so idle-close can fire) and surface the failure
      // to the caller, which falls back to the legacy polling loop.
      this.#onResolverRemoved(key);
      throw e;
    }
    // A rejection of the one-shot immediate check means the JOB errored (status endpoint
    // returned an error response), not that the SSE path failed. Re-tag it as an
    // ImmediateCheckError so the caller can fall back without logging an SSE fallback.
    // (Resolving null yields PENDING_FOREVER, so .catch only ever sees a real rejection.)
    const fromCheck = immediateCheck()
      .then((r) => r ?? PENDING_FOREVER)
      .catch((e: unknown): never => {
        throw new ImmediateCheckError(e);
      });

    // Safety net: re-read the backend store on a timer. The SSE event is best-effort;
    // the store is the source of truth, so a missed/late event is recovered here within
    // one interval. Non-blocking (streamImmediate) — does not reintroduce a holding poll.
    let probeTimer: ReturnType<typeof setInterval> | null = null;
    const fromProbe = new Promise<JobReady>((resolve) => {
      probeTimer = setInterval(() => {
        void immediateCheck()
          .then((r) => {
            if (r !== null) {
              resolve(r);
            }
          })
          .catch(() => {
            // swallow: a transient probe failure must not abort the SSE wait
          });
      }, STREAM_FASTPATH_PROBE_INTERVAL_MS);
      // Hand the timer to the resolver entry too: on the caller-bail path the caller
      // settles its OWN race and calls cancelJob(key) — awaitJob's promise never settles
      // and the finally below never runs, so #onResolverRemoved is the only place that can
      // clear this interval there. (The entry may already be gone if a ready event won
      // during #subscribe; the finally's local clear covers that path.)
      const entry = this.#resolvers.get(key);
      if (entry !== undefined) {
        entry.probeTimer = probeTimer;
      }
    });

    try {
      return await Promise.race([fromEvent, fromCheck, fromProbe]);
    } finally {
      if (probeTimer !== null) {
        clearInterval(probeTimer);
      }
      // If the ready EVENT won, its listener already removed the resolver + scheduled
      // the idle timer; only clean up here if the resolver is still present (immediate
      // check or probe won the race), to avoid restarting the idle timer.
      if (this.#resolvers.has(key)) {
        this.#onResolverRemoved(key);
      }
    }
  }

  #onResolverRemoved(key: string): void {
    const entry = this.#resolvers.get(key);
    if (entry !== undefined && entry.probeTimer !== null) {
      clearInterval(entry.probeTimer);
    }
    this.#resolvers.delete(key);
    this.#armIdleCloseIfIdle();
  }

  #armIdleCloseIfIdle(): void {
    if (this.#resolvers.size === 0) {
      this.#scheduleIdleClose();
    }
  }

  #scheduleIdleClose(): void {
    this.#cancelIdleTimer();
    this.#idleTimer = setTimeout(() => {
      this.#idleTimer = null;
      if (this.#resolvers.size === 0) {
        this.#closeConnection();
      }
    }, IDLE_CLOSE_DELAY_MS);
  }

  #cancelIdleTimer(): void {
    if (this.#idleTimer !== null) {
      clearTimeout(this.#idleTimer);
      this.#idleTimer = null;
    }
  }

  #closeConnection(): void {
    this.#es?.close();
    this.#es = null;
    this.#connId = null;
    this.#connecting = null;
    this.#connectResolve = null;
    this.#token = null;
    this.#ns = null;
  }

  /**
   * POSTs a /subscribe request for `key` using the current connId and token.
   * Throws when the gateway rejects the subscription (non-2xx) — fetch resolves on
   * HTTP errors, so without this check a rejected subscribe would leave the caller
   * waiting on a ready event that can never arrive. 202 is a success: the gateway
   * forwarded the registration to the task holding this connection.
   */
  async #subscribe(key: string): Promise<void> {
    if (!this.#ns) {
      return;
    }
    const channel = `job:${this.#ns}:${key}`;
    const res = await this.deps.fetchFn(`${this.#gatewayBaseUrl}/subscribe`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.#token}`,
      },
      body: JSON.stringify({connId: this.#connId, channel}),
    });
    if (!res.ok) {
      throw new SubscribeRejectedError(res.status);
    }
  }

  /**
   * Re-subscribes every pending key after a reconnect and runs an immediateCheck
   * for each to catch completions that fired during the disconnect window.
   * Fire-and-forget; the fast-path timeout remains the ultimate backstop.
   */
  async #resubscribeAll(): Promise<void> {
    if (!this.#ns) {
      return;
    }
    const entries = Array.from(this.#resolvers.entries());
    await Promise.allSettled(
      entries.map(async ([key, entry]) => {
        // A rejected re-subscribe must not skip the immediate check below — it is
        // what recovers completions that fired during the disconnect window. The
        // per-job probe and the caller's overall timeout still backstop delivery,
        // but log it: without a signal this is an invisible degraded state (job
        // waits on the 30s probe with no subscription live).
        await this.#subscribe(key).catch((e: unknown) => {
          this.#reportResubscribeFailure(key, e);
        });
        const result = await entry.immediateCheck();
        if (result !== null && this.#resolvers.has(key)) {
          entry.resolve(result);
          this.#onResolverRemoved(key);
        }
      })
    );
  }

  /** Fire-and-forget clientlog line for a re-subscribe the gateway rejected after a reconnect. */
  #reportResubscribeFailure(key: string, e: unknown): void {
    try {
      const statusPart = e instanceof SubscribeRejectedError ? ` status=${e.status}` : '';
      window.PMW.log(`stream-fastpath-resubscribe-failed key=${key}${statusPart}`);
    } catch {
      // swallow: telemetry must never affect the reconnect recovery path
    }
  }

  #ensureConnected(): Promise<void> {
    if (this.#connId !== null) {
      return Promise.resolve();
    }
    if (this.#connecting !== null) {
      return this.#connecting;
    }
    const connecting = new Promise<void>((resolve, reject) => {
      this.#connectResolve = resolve;
      void this.deps.getToken().then(({token, ns}) => {
        this.#token = token;
        this.#ns = ns;
        const es = this.deps.eventSourceFactory(
          `${this.#gatewayBaseUrl}/stream?token=${encodeURIComponent(token)}`
        );
        this.#es = es;
        es.addEventListener('connected', (e) => {
          const {connId} = JSON.parse(e.data as string) as ConnectedEvent;
          const isReconnect = this.#connId !== null;
          this.#connId = connId;
          if (this.#connectResolve !== null) {
            this.#connectResolve();
            this.#connectResolve = null;
          }
          if (isReconnect) {
            void this.#resubscribeAll();
          }
        });
        es.addEventListener('ready', (e) => {
          const data = JSON.parse(e.data as string) as JobReady;
          const entry = this.#resolvers.get(data.key);
          if (entry !== undefined) {
            entry.resolve(data);
            this.#onResolverRemoved(data.key);
          }
        });
        es.addEventListener('error', () => {
          reject(new Error('sse-error'));
        });
      }).catch(reject);
    });
    // If the INITIAL connect fails (token fetch throws, or an EventSource 'error'
    // fires before 'connected'), tear down so a later awaitJob retries from scratch
    // instead of reusing this rejected promise for the rest of the page session.
    // After a successful connect the promise is already resolved, so a later
    // mid-stream 'error' is a no-op here and the browser auto-reconnects.
    connecting.catch(() => {
      if (this.#connecting === connecting) {
        this.#closeConnection();
      }
    });
    this.#connecting = connecting;
    return connecting;
  }
}
